import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  ScanLine,
  Mic,
  FileText,
  Sparkles,
} from "lucide-react";
import { RADAGENT_URL, RADSCRIBE_URL } from "@/lib/constants";

const PRODUCTS = [
  {
    id: "radagent",
    eyebrow: "Diagnostic Reporting",
    title: "RadAgent",
    subtitle: "AI-Powered Radiology Reporting",
    tagline: "From scan to signed report in minutes.",
    description:
      "RadAgent transforms radiology workflows by generating structured, clinically aligned reports from imaging studies, helping diagnostic centres improve turnaround times while maintaining quality and consistency.",
    benefits: [
      "Faster report generation",
      "Reduced reporting backlog",
      "Consistent structured reporting",
      "Increased radiologist productivity",
    ],
    icon: ScanLine,
    accent: FileText,
    href: RADAGENT_URL,
    testId: "radagent",
  },
  {
    id: "radscribe",
    eyebrow: "Voice Intelligence",
    title: "RadScribe",
    subtitle: "Voice-to-Report Intelligence",
    tagline: "Speak naturally. Report instantly.",
    description:
      "RadScribe converts radiologists' spoken findings into complete reports using the practice's preferred templates, terminology, and reporting style. Reports are generated ready for review and sign-off.",
    benefits: [
      "Eliminates manual transcription",
      "Preserves reporting style and templates",
      "Reduces administrative workload",
      "Accelerates clinical workflows",
    ],
    icon: Mic,
    accent: Sparkles,
    href: RADSCRIBE_URL,
    testId: "radscribe",
  },
];

function ProductCard({ product, index }) {
  const Icon = product.icon;
  const Accent = product.accent;

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.article
      data-testid={`product-card-${product.testId}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-120px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMouseMove}
      className="product-card group relative bg-white border border-[#6C7A89]/15 rounded-3xl p-8 sm:p-10 lg:p-12"
    >
      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none opacity-[0.07]">
        <Accent className="w-full h-full text-[#14D6C7]" strokeWidth={0.8} />
      </div>

      <div className="relative flex flex-col gap-6">
        {/* Icon block */}
        <div className="relative inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#14D6C7]/10 to-[#2EE8FF]/5 border border-[#14D6C7]/20">
          <Icon className="w-6 h-6 text-[#14D6C7]" strokeWidth={1.8} />
          <span className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#14D6C7]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-md -z-10" />
        </div>

        {/* Eyebrow */}
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#14D6C7] font-manrope font-bold">
          <span className="block w-5 h-px bg-[#14D6C7]" />
          {product.eyebrow}
        </div>

        {/* Title & subtitle */}
        <div>
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-tight text-[#071B34] leading-[1.05]">
            {product.title}
          </h3>
          <p className="mt-3 text-lg sm:text-xl font-manrope font-medium text-[#2B3440]">
            {product.subtitle}
          </p>
          <p className="mt-2 text-base font-poppins text-[#14D6C7]/90 font-medium italic">
            {product.tagline}
          </p>
        </div>

        {/* Description */}
        <p className="text-[15px] leading-relaxed font-poppins text-[#6C7A89]">
          {product.description}
        </p>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-[#6C7A89]/20 via-[#6C7A89]/10 to-transparent" />

        {/* Benefits */}
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#6C7A89] font-manrope font-semibold mb-4">
            Key Benefits
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {product.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm font-poppins text-[#2B3440]">
                <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#14D6C7]/12 text-[#14D6C7]">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="pt-4">
          <a
            data-testid={`${product.testId}-learn-more-btn`}
            href={product.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-manrope font-semibold text-[#071B34] group/cta"
          >
            <span className="link-underline">Learn More</span>
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#071B34] text-[#14D6C7] transition-all duration-300 group-hover/cta:bg-[#14D6C7] group-hover/cta:text-[#071B34] group-hover/cta:rotate-[-12deg]">
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
            </span>
          </a>
        </div>
      </div>
    </motion.article>
  );
}

export default function Products() {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-[#F9FBFD] py-28 sm:py-36"
    >
      {/* Decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(108,122,137,0.18) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent, black 20%, black 80%, transparent)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-16"
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-5">
            <span className="block w-8 h-px bg-[#14D6C7]" />
            Products
          </div>
          <h2 className="text-balance text-4xl sm:text-5xl lg:text-6xl font-manrope font-bold tracking-[-0.02em] text-[#071B34] leading-[1.05]">
            Intelligence that meets clinicians{" "}
            <span className="text-[#14D6C7]">where they work.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg font-poppins text-[#6C7A89] max-w-2xl">
            Purpose-built systems that integrate with existing workflows — not replace them.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
